
import React, { useState } from 'react';
import { MdsItem, ItemDetail, Status, Millsheet } from '../types';
import ItemDetailModal from './ItemDetailModal';

// --- ADD NODE MODAL ---
interface AddNodeModalProps {
  parentId: string | null;
  level: number;
  onClose: () => void;
  onSave: (data: Partial<MdsItem>) => void;
}

const AddNodeModal: React.FC<AddNodeModalProps> = ({ parentId, level, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    part_number_customer: '',
    part_name_customer: '',
    material: '',
    manufacturer: '',
    qty_r: 1,
    qty_l: 0,
    uom: 'PC'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.part_number_customer || !formData.part_name_customer) {
      alert("Please enter both Part Number and Name.");
      return;
    }
    onSave({
      ...formData,
      parent_part_number: parentId || '',
      level,
      status: Status.NEW,
      millsheet: Millsheet.X
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[250] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200 dark:border-slate-700">
        <div className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white leading-none">
            {parentId ? 'Register Child Component' : 'Initialize Root Assy'}
          </h3>
          <p className="text-xs text-slate-500 mt-2">
            {parentId ? `Parent Link: ${parentId} (Level ${level})` : 'New Top Level Assembly'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">Part Number Customer</label>
              <input 
                required
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="e.g. HON-102-X"
                value={formData.part_number_customer}
                onChange={e => setFormData({...formData, part_number_customer: e.target.value.toUpperCase()})}
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">Part Name</label>
              <input 
                required
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="e.g. BRACKET ASSEMBLY"
                value={formData.part_name_customer}
                onChange={e => setFormData({...formData, part_name_customer: e.target.value.toUpperCase()})}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">Material</label>
              <input 
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="e.g. SPHC 2.0"
                value={formData.material}
                onChange={e => setFormData({...formData, material: e.target.value.toUpperCase()})}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">Manufacturer</label>
              <input 
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                placeholder="e.g. Vendor A"
                value={formData.manufacturer}
                onChange={e => setFormData({...formData, manufacturer: e.target.value.toUpperCase()})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">Qty R</label>
                <input 
                  type="number"
                  min="0"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={formData.qty_r}
                  onChange={e => setFormData({...formData, qty_r: parseInt(e.target.value) || 0})}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">Qty L</label>
                <input 
                  type="number"
                  min="0"
                  className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  value={formData.qty_l}
                  onChange={e => setFormData({...formData, qty_l: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1.5">UOM</label>
              <select 
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={formData.uom}
                onChange={e => setFormData({...formData, uom: e.target.value})}
              >
                <option value="PC">PC</option>
                <option value="SET">SET</option>
                <option value="KG">KG</option>
                <option value="M">M</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-100 dark:border-slate-700 mt-6">
            <button 
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 shadow-sm transition-colors"
            >
              Confirm Component
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- TREE ITEM COMPONENT ---
interface TreeItemProps {
  item: MdsItem;
  onAddChild: (parentId: string, level: number) => void;
  onDelete: (partNumber: string) => void;
  onEdit: (item: MdsItem) => void;
  onUpdateDetails: (partNumber: string, details: ItemDetail) => void;
  isLastChild?: boolean;
}

const TreeItem: React.FC<TreeItemProps> = ({ item, onAddChild, onDelete, onEdit, onUpdateDetails, isLastChild }) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const isRoot = item.level === 0;
  const hasChildren = item.children && item.children.length > 0;

  return (
    <div className="relative">
      <div className="flex items-start gap-4 relative">
        {!isRoot && (
          <>
            <div className="absolute -left-8 top-6 w-8 h-[2px] bg-slate-200 dark:bg-slate-700"></div>
            <div className={`absolute -left-8 w-[2px] bg-slate-200 dark:bg-slate-700 ${isLastChild ? 'h-6 bottom-auto top-0' : 'h-full top-0'}`}></div>
          </>
        )}

        <div className={`mt-[22px] w-2.5 h-2.5 rounded-full border-2 border-white dark:border-slate-800 shadow-sm z-10 flex-shrink-0 ${isRoot ? 'bg-blue-600 ring-4 ring-blue-100 dark:ring-blue-900/30' : 'bg-slate-300 dark:bg-slate-600'}`}></div>
        
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm p-4 min-w-[360px] mb-4 transition-all hover:shadow-md hover:border-blue-200 dark:hover:border-blue-900 group relative overflow-hidden">
          <div className="absolute top-3 right-3 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-[10px] font-bold uppercase rounded-full">
            LVL {item.level}
          </div>

          <div className="flex justify-between items-start mb-1 pr-12">
            <h4 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">{item.part_number_customer}</h4>
          </div>
          
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase truncate max-w-[280px] mb-3">
            {item.part_name_customer}
          </p>

          <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-lg border border-slate-100 dark:border-slate-700/50">
            <div className="flex gap-4">
               <div>
                 <span className="text-[10px] font-medium text-slate-400 uppercase block">Qty</span>
                 <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{item.qty_total} {item.uom}</span>
               </div>
               <div>
                 <span className="text-[10px] font-medium text-slate-400 uppercase block">Weight</span>
                 <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">{item.berat_total.toFixed(2)}g</span>
               </div>
            </div>
            
            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
               <button 
                 onClick={() => onAddChild(item.part_number_customer, item.level + 1)} 
                 className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors" 
                 title="Add Child"
               >
                 <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
               </button>
               <button onClick={() => setShowDetailModal(true)} className="p-1.5 bg-slate-100 text-slate-600 rounded hover:bg-slate-200 transition-colors" title="Details">
                 <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
               </button>
               <button 
                 onClick={() => onDelete(item.part_number_customer)} 
                 className="p-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors" 
                 title="Delete"
               >
                 <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
               </button>
            </div>
          </div>
        </div>
      </div>

      {showDetailModal && (
        <ItemDetailModal 
          item={item} 
          onClose={() => setShowDetailModal(false)} 
          onSave={(details) => {
            onUpdateDetails(item.part_number_customer, details);
            setShowDetailModal(false);
          }} 
        />
      )}

      {hasChildren && (
        <div className="ml-8 border-l border-slate-200 dark:border-slate-700 pl-6">
          {item.children?.map((child, idx) => (
            <TreeItem 
              key={`${child.part_number_customer}-${idx}`} 
              item={child} 
              onAddChild={onAddChild} 
              onDelete={onDelete} 
              onEdit={onEdit} 
              onUpdateDetails={onUpdateDetails}
              isLastChild={idx === (item.children?.length || 0) - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// --- MAIN TREE VIEW ---
interface TreeViewProps {
  tree: MdsItem[];
  onAddChild: (data: Partial<MdsItem>) => void;
  onDelete: (partNumber: string) => void;
  onEdit: (item: MdsItem) => void;
  onUpdateDetails: (partNumber: string, details: ItemDetail) => void;
}

const TreeView: React.FC<TreeViewProps> = ({ tree, onAddChild, onDelete, onEdit, onUpdateDetails }) => {
  const [modalState, setModalState] = useState<{ parentId: string | null, level: number } | null>(null);

  const handleOpenAddModal = (parentId: string | null, level: number) => {
    setModalState({ parentId, level });
  };

  const handleSaveNode = (data: Partial<MdsItem>) => {
    onAddChild(data);
    setModalState(null);
  };

  return (
    <div className="bg-slate-50/50 dark:bg-slate-900/30 p-8 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto min-h-[600px] relative">
      <div className="mb-8 flex justify-end sticky top-0 z-20">
        <button 
          onClick={() => handleOpenAddModal(null, 0)} 
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium shadow-sm hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
          New Top Assembly
        </button>
      </div>

      {modalState && (
        <AddNodeModal 
          parentId={modalState.parentId}
          level={modalState.level}
          onClose={() => setModalState(null)}
          onSave={handleSaveNode}
        />
      )}

      {tree.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
           <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
             <svg className="w-8 h-8 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
           </div>
           <p className="font-medium text-sm">System Map is Empty</p>
           <p className="text-xs text-slate-400 mt-1">Start by creating a root assembly</p>
           <button onClick={() => handleOpenAddModal(null, 0)} className="mt-6 px-6 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">Initialize Root</button>
        </div>
      ) : (
        <div className="inline-block min-w-full pl-4">
          {tree.map((assy, idx) => (
            <div key={`${assy.part_number_customer}-${idx}`} className="mb-8 last:mb-0">
               <TreeItem item={assy} onAddChild={handleOpenAddModal} onDelete={onDelete} onEdit={onEdit} onUpdateDetails={onUpdateDetails} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TreeView;
